import HistoryValue from '@components/history-value/history-value.component'
import { withTransaction } from '@elastic/apm-rum-react'
import {
  Accordion,
  CardPaymentValue,
  Collapse,
  FinancialOperationService,
  HistoryTransaction,
  InfinityScroll,
  Loader,
  LogService,
  User,
  onLangChange,
  onUserChange,
} from '@hub/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import './payments-history.page.scoped.scss'
import './payments-history.page.scss'
//@ts-ignore
import { paymentHistory } from '@hub/shared/services/financial-operation/financial-operation.service'

interface Props {
  year: string
  total: number
}

const PaymentsHistoryPageComponent = () => {
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const logService = useMemo(() => new LogService(), [])
  const financialOperationService = useMemo(() => new FinancialOperationService(), [])

  logService.init('Payment History Page')

  const [t] = useTranslation('paymentsHistory')
  const onLangChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [lang, setLang] = useState<string>('en')
  const [loading, setLoading] = useState(false)
  const [loadingList, setLoadingList] = useState(false)
  const [nextPage, setNextPage] = useState(1)
  const [transactions, setTransactions] = useState<HistoryTransaction[]>([])
  const [transactionsByYear, setTransactionsByYear] = useState<Array<any>>([])
  const [hideValue, setHideValue] = useState(true)
  const navigate = useNavigate()

  const groupByDate = useCallback((array: Array<HistoryTransaction>) => {
    return array.reduce((groups: any, item: HistoryTransaction) => {
      const newKey = item['dueDate'].substring(0, 4)
      if (!groups[newKey]) {
        groups[newKey] = []
      }
      groups[newKey].push(item)
      return groups
    }, {})
  }, [])

  const getReceivedHistory = useCallback(() => {
    setLoadingList(true)
    financialOperationService
      //@ts-ignore
      .getPaymentHistory('received', nextPage, 10)
      .then((e: paymentHistory) => {
        const newArray = transactions.concat(e?.transactions)
        setNextPage(newArray.length < e?.total ? nextPage + 1 : 0)
        setTransactions(newArray)
        setTransactionsByYear(Object.entries(groupByDate(newArray)).reverse())
      })
      .catch(() => {
        setNextPage(0)
        setTransactions([])
        setTransactionsByYear([])
        logService.error({ message: 'Failed to get Payment History' })
      })
      .finally(() => {
        setLoading(false)
        setLoadingList(false)
      })
  }, [financialOperationService, nextPage, transactions, groupByDate, logService])

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path)
    },
    [navigate]
  )

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (user: User) => {
      setLoading(true)
      if (user?.squidId) {
        getReceivedHistory()
      }
    })

    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUserChangeSubscription])

  useEffect(() => {
    onLangChangeSubscription.current = onLangChange.subscribe((lang) => {
      setLang(lang)
    })

    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [])

  const getTotalValue = useCallback((array: Array<HistoryTransaction>) => {
    const getTotal = (total: number, item: HistoryTransaction) => {
      return total + item.netValue
    }
    return array.reduce(getTotal, 0)
  }, [])

  const Header = ({ year, total }: Props) => {
    return (
      <div className="header">
        <h1>{year}</h1>
        <p style={hideValue ? { fontSize: '1rem' } : {}}>
          <span>{t('total')}</span>
          {hideValue ? '••••••' : total.toLocaleString('pt-br', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 })}
        </p>
      </div>
    )
  }

  return (
    <div className="payment-history-page box">
      <section className="py-3">
        <HistoryValue onChange={(value) => setHideValue(value)} />
      </section>
      <section className="content">
        {loading ? (
          <div className="display-flex justify-content-center">
            <Loader />
          </div>
        ) : transactions?.length > 0 ? (
          <InfinityScroll
            className="payment-info-page-infinity-scroll"
            loading={loadingList}
            onLoadMore={getReceivedHistory}
            hasMore={!!nextPage}
            endMessage={' '}
          >
            <Accordion>
              {transactionsByYear?.map((group, index) => (
                <Collapse key={index} title={<Header year={group[0]} total={getTotalValue(group[1])} />}>
                  <div className="cards">
                    {group[1]?.map((transaction: HistoryTransaction) => (
                      <CardPaymentValue
                        key={transaction?.transactionId}
                        paymentObject={transaction}
                        hideValue={hideValue}
                        onNavigateTo={handleNavigate}
                      />
                    ))}
                  </div>
                </Collapse>
              ))}
            </Accordion>
          </InfinityScroll>
        ) : (
          <div className="no-history">
            <i className="fa-regular fa-sack-dollar gray" style={{ fontSize: '2.5rem' }}></i>
            <p>{t('noHistory')}</p>
          </div>
        )}
      </section>
    </div>
  )
}

export default withTransaction('PaymentsHistoryPageComponent', 'component')(PaymentsHistoryPageComponent)
